import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { toast } from 'sonner'
import { PackageSearch, Plus, Search, Power, Pencil, Camera, Loader2, X, Trash2, StickyNote, History, Ban, RotateCcw, Truck, MessageSquare, PlusCircle } from 'lucide-react'
import DashboardLayout from '../../layouts/DashboardLayout'
import { cn } from '../../lib/utils'

const INPUT = 'w-full rounded-lg border border-brand-border bg-white px-2.5 py-1.5 text-sm focus:border-brand-primary focus:outline-none'
const BLANK = { ref: '', name: '', storage_area: 'Uncategorised', category: '', supplier: '', supplier_code: '', barcode: '', unit: '', par_level: '' }

// OWNER-ONLY catalogue management. Ivory is the single source of truth; Filing only consumes the export.
export default function Catalogue({ items, areas, categories = [], is_owner = true }) {
  const [q, setQ] = useState('')
  const [showInactive, setShowInactive] = useState(false)
  const [expiringOnly, setExpiringOnly] = useState(false)
  const [noPhotoOnly, setNoPhotoOnly] = useState(false)
  const [catFilter, setCatFilter] = useState('') // '' = all categories
  const [editing, setEditing] = useState(null) // id or 'new'
  const [uploading, setUploading] = useState(null) // item id mid-upload
  const [viewing, setViewing] = useState(null)     // item whose photo gallery is open
  const [notesItem, setNotesItem] = useState(null) // item whose notes editor is open
  const [historyItem, setHistoryItem] = useState(null) // item whose history timeline is open
  // Local working copy so cancel/restore + per-photo notes update + re-sort live (server persists them,
  // and a router.reload of `items` re-syncs this via the effect below).
  const [rows, setRows] = useState(items)
  useEffect(() => { setRows(items) }, [items])
  const cameraRef = useRef(null)
  const shootTargetRef = useRef(null)
  const expiringCount = useMemo(() => items.filter((i) => i.expiring_soon).length, [items])
  // How much of the catalogue still has no picture — the thing that tells Paul whether this is done.
  const noPhotoCount = useMemo(() => items.filter((i) => i.active && !i.photo_url).length, [items])
  // Cancelled rows sink to the BOTTOM (stable within their group; Array.sort is stable).
  const filtered = useMemo(() => rows.filter((i) =>
    (showInactive || i.active) &&
    (!expiringOnly || i.expiring_soon) &&
    (!noPhotoOnly || !i.photo_url) &&
    (!catFilter || (i.category_label || i.category || 'Uncategorised') === catFilter) &&
    (q === '' || `${i.name} ${i.ref} ${i.supplier} ${i.supplier_code} ${i.barcode}`.toLowerCase().includes(q.toLowerCase()))
  ).sort((a, b) => (a.cancelled ? 1 : 0) - (b.cancelled ? 1 : 0)), [rows, q, showInactive, expiringOnly, noPhotoOnly, catFilter])

  function openCamera(item) {
    shootTargetRef.current = item
    if (cameraRef.current) { cameraRef.current.value = ''; cameraRef.current.click() }
  }

  async function onPhotoPicked(ev) {
    const file = ev.target.files?.[0]
    const item = shootTargetRef.current
    shootTargetRef.current = null
    if (!file || !item) return
    setUploading(item.id)
    try {
      const blob = await downscale(file)
      const fd = new FormData()
      fd.append('photo', blob, 'stock.jpg')
      const res = await fetch(`/stock_items/${item.id}/photo`, {
        method: 'POST', headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' }, body: fd,
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`)
      toast.success('Photo saved')
      setViewing((v) => (v && v.id === item.id) ? { ...v, photo_url: data.photo_url, photos: data.photos } : v)
      router.reload({ only: ['items'] })
    } catch (err) {
      toast.error(`Photo not saved — ${err.message || 'try again'}`)
    } finally {
      setUploading(null)
    }
  }

  // Owner-only per-photo delete (the gallery's red X). Purges THAT attachment; the response carries the
  // remaining photos[] so the open viewer's filmstrip updates live (and closes if none remain), while
  // the table reloads underneath.
  async function removePhotoById(item, photoId) {
    if (!is_owner) return
    if (!confirm('Delete this photo? It will be logged.')) return
    try {
      const res = await fetch(`/stock_items/${item.id}/photo/${photoId}`, {
        method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setViewing((v) => (v && v.id === item.id)
        ? ((data.photos && data.photos.length) ? { ...v, photo_url: data.photo_url, photos: data.photos } : null)
        : v)
      toast.success('Photo removed')
      router.reload({ only: ['items'] })
    } catch { toast.error('Could not remove the photo') }
  }

  // Owner edits the persistent item note here (counters write it on the count screen). JSON endpoint,
  // so a raw fetch; the table is reloaded once when the editor closes, not on every keystroke.
  async function saveNotes(item, text) {
    try {
      const res = await fetch(`/stock_items/${item.id}/notes`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
        body: JSON.stringify({ notes: text }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setNotesItem((n) => (n && n.id === item.id) ? { ...n, notes: data.notes, notes_updated_at: data.notes_updated_at, notes_updated_by: data.notes_updated_by } : n)
      return true
    } catch (err) { toast.error(`Note not saved — ${err.message || 'try again'}`); return false }
  }

  // Cancel / restore an item (reversible, EVERYONE). Trust the server's `cancelled` + `logs`, never body
  // text; update the row locally + re-sort (cancelled sinks to the bottom).
  async function toggleCancel(item) {
    const next = !item.cancelled
    // stockdno1019: RESTORE asks first (a cancelled item is DO NOT ORDER; restoring puts it back in the ordering export).
    if (!next && !window.confirm(`Restore ${item.name}? It becomes orderable again.`)) return
    try {
      const res = await fetch(`/stock_items/${item.id}/cancellation`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
        body: JSON.stringify({ cancelled: next }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setRows((prev) => prev.map((r) => r.id === item.id
        ? { ...r, cancelled: data.cancelled, cancelled_at: data.cancelled_at, cancelled_by: data.cancelled_by, logs: data.logs ?? r.logs }
        : r))
      setHistoryItem((h) => (h && h.id === item.id) ? { ...h, cancelled: data.cancelled, logs: data.logs ?? h.logs } : h)
      toast.success(data.cancelled ? 'Item cancelled' : 'Item restored')
    } catch (err) { toast.error(`Could not update — ${err.message || 'try again'}`) }
  }

  // Per-photo note (EVERYONE). Lives on the photo; the returned photos[] fans out to the row + the viewer.
  async function savePhotoNote(item, photoId, note) {
    try {
      const res = await fetch(`/stock_items/${item.id}/photo/${photoId}/note`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
        body: JSON.stringify({ note }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setRows((prev) => prev.map((r) => r.id === item.id ? { ...r, photos: data.photos } : r))
      setViewing((v) => (v && v.id === item.id) ? { ...v, photos: data.photos } : v)
      return true
    } catch (err) { toast.error(`Note not saved — ${err.message || 'try again'}`); return false }
  }

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-5xl">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <PackageSearch className="text-brand-primary" />
            <div>
              <h1 className="text-xl font-semibold text-brand-ink">Stock Catalogue</h1>
              <p className="text-sm text-brand-muted">{items.filter((i) => i.active).length} active · {items.length} total</p>
            </div>
          </div>
          <button onClick={() => setEditing('new')} className="flex items-center gap-2 rounded-xl bg-brand-primary px-4 py-2 text-sm font-semibold text-white"><Plus size={16} /> Add item</button>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / ref / supplier / barcode"
              className="w-full rounded-xl border border-brand-border bg-white py-2 pl-9 pr-3 text-sm focus:border-brand-primary focus:outline-none" />
          </div>
          <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)}
            className="rounded-xl border border-brand-border bg-white px-3 py-2 text-sm text-brand-ink focus:border-brand-primary focus:outline-none">
            <option value="">All categories</option>
            {[...categories, 'Uncategorised'].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-brand-muted">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> show inactive
          </label>
          <button type="button" onClick={() => setExpiringOnly((v) => !v)}
            className={cn('inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-sm font-medium transition-colors',
              expiringOnly ? 'border-red-300 bg-red-50 text-red-700' : 'border-brand-border bg-white text-brand-muted hover:bg-brand-surface')}>
            Expiring soon{expiringCount > 0 && <span className={cn('rounded-full px-1.5 text-xs font-semibold', expiringOnly ? 'bg-red-200 text-red-800' : 'bg-red-100 text-red-700')}>{expiringCount}</span>}
          </button>
          <button type="button" onClick={() => setNoPhotoOnly((v) => !v)}
            className={cn('inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-sm font-medium transition-colors',
              noPhotoOnly ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-brand-border bg-white text-brand-muted hover:bg-brand-surface')}>
            <Camera size={14} /> No photo{noPhotoCount > 0 && <span className={cn('rounded-full px-1.5 text-xs font-semibold', noPhotoOnly ? 'bg-amber-200 text-amber-900' : 'bg-amber-100 text-amber-700')}>{noPhotoCount}</span>}
          </button>
        </div>

        {editing === 'new' && <EditRow initial={BLANK} categories={categories} onDone={() => setEditing(null)} isNew />}

        <div className="overflow-x-auto rounded-2xl border border-brand-border bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-brand-border text-left text-xs uppercase tracking-wide text-brand-muted">
              <tr><th className="px-3 py-2">Photo</th><th className="px-3 py-2">Item</th><th className="px-3 py-2">Ref</th><th className="px-3 py-2">Supplier</th><th className="px-3 py-2">Category</th><th className="px-3 py-2">Area</th><th className="px-3 py-2">Par</th><th className="px-3 py-2">Last count</th><th className="px-3 py-2">Expiry</th><th className="px-3 py-2"></th></tr>
            </thead>
            <tbody className="divide-y divide-brand-border">
              {filtered.map((i) => editing === i.id
                ? <tr key={i.id}><td colSpan={10} className="p-0"><EditRow initial={i} categories={categories} onDone={() => setEditing(null)} /></td></tr>
                : (
                  <tr key={i.id} className={cn(!i.active && 'opacity-50', i.expiring_soon && !i.cancelled && 'bg-red-50/60', i.cancelled && 'bg-red-50 shadow-[inset_4px_0_0_0_#ef4444]')}>
                    <td className="px-3 py-2">
                      <PhotoCell item={i} busy={uploading === i.id} onShoot={() => openCamera(i)} onView={() => setViewing(i)} />
                    </td>
                    <td className="px-3 py-2 font-medium text-brand-ink">
                      <span className={cn(i.cancelled && 'text-red-700 line-through decoration-red-500')}>{i.name}{i.unit && <span className="text-brand-muted"> · {i.unit}</span>}</span>
                      {i.cancelled && <span data-stk-dno className="ml-2 whitespace-nowrap rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">DO NOT ORDER</span>}
                      {i.cancelled && <DoNotOrderNote notes={i.notes} />}
                    </td>
                    <td className="px-3 py-2 text-brand-muted">{i.ref}</td>
                    <td className="px-3 py-2 text-brand-muted">{i.supplier}{i.supplier_code && ` (${i.supplier_code})`}</td>
                    <td className="px-3 py-2 text-brand-muted">{i.category_label || i.category || <span className="text-amber-600">Uncategorised</span>}</td>
                    <td className="px-3 py-2 text-brand-muted">{i.storage_area}</td>
                    <td className="px-3 py-2">{i.par_level}</td>
                    <td className="px-3 py-2 text-brand-muted">{i.last_counted_in_stock ?? '—'}</td>
                    <td className="px-3 py-2">
                      {i.last_expires_on
                        ? <span className={cn('whitespace-nowrap', i.expiring_soon ? 'font-semibold text-red-600' : 'text-brand-muted')}>{i.last_expires_on}{i.expiring_soon && ' ⚠'}</span>
                        : <span className="text-brand-muted/50">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => setNotesItem(i)} className={cn('rounded-lg p-1.5 hover:bg-brand-surface', i.notes ? 'text-amber-600' : 'text-brand-muted')} title={i.notes ? 'Note' : 'Add note'}><StickyNote size={15} /></button>
                        <button onClick={() => setHistoryItem(i)} className="rounded-lg p-1.5 text-brand-muted hover:bg-brand-surface" title="History"><History size={15} /></button>
                        <button onClick={() => toggleCancel(i)} className={cn('rounded-lg p-1.5 hover:bg-brand-surface', i.cancelled ? 'text-emerald-600' : 'text-brand-muted')} title={i.cancelled ? 'Restore' : 'Cancel'}>{i.cancelled ? <RotateCcw size={15} /> : <Ban size={15} />}</button>
                        <button onClick={() => setEditing(i.id)} className="rounded-lg p-1.5 text-brand-muted hover:bg-brand-surface" title="Edit"><Pencil size={15} /></button>
                        <button onClick={() => toggleActive(i)} className="rounded-lg p-1.5 text-brand-muted hover:bg-brand-surface" title={i.active ? 'Deactivate' : 'Reactivate'}><Power size={15} className={i.active ? '' : 'text-emerald-500'} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={onPhotoPicked} className="hidden" />
      {viewing && <PhotoViewer item={viewing} isOwner={is_owner} onClose={() => setViewing(null)}
        onAddPhoto={() => openCamera(viewing)}
        onDeletePhoto={(pid) => removePhotoById(viewing, pid)}
        onSavePhotoNote={(pid, note) => savePhotoNote(viewing, pid, note)} />}
      {notesItem && <NotesEditor item={notesItem} canEdit={is_owner}
        onSave={(text) => saveNotes(notesItem, text)}
        onClose={() => { setNotesItem(null); router.reload({ only: ['items'] }) }} />}
      {historyItem && <HistorySheet item={historyItem} onClose={() => setHistoryItem(null)} />}
    </DashboardLayout>
  )
}

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}

// stockdno1019 (Paul 19 Oct): a cancelled item is a DO NOT ORDER product — WHY inline under its name, WRAPPED (never a
// one-line truncate); the note's closing "Correct product: …" / "Correct: …" clause is split out and never clamped. Same
// rule as Count.jsx (kept local: each page is its own Inertia entry). The amber note icon still opens the full text.
function dnoNote(notes) {
  const line = String(notes || '').split('\n').map((s) => s.trim()).find(Boolean) || ''
  const m = line.match(/^(.*?)\s*(\bCorrect(?: product)?:\s*\S.*)$/i)
  return m ? { why: m[1].replace(/[\s.,;:-]+$/, ''), correct: m[2] } : { why: line, correct: '' }
}

function DoNotOrderNote({ notes }) {
  const { why, correct } = dnoNote(notes)
  if (!why && !correct) return null
  return (
    <div data-stk-dno-note className="mt-1 max-w-md text-[11px] font-normal leading-snug text-red-700">
      {why && <p className="line-clamp-3 whitespace-normal break-words">{why}</p>}
      {correct && <p className="mt-0.5 whitespace-normal break-words font-semibold text-red-800">{correct}</p>}
    </div>
  )
}

function PhotoCell({ item, busy, onShoot, onView }) {
  const base = 'flex h-11 w-11 items-center justify-center rounded-lg'
  if (busy) return <div className={cn(base, 'border border-brand-border bg-brand-surface text-brand-muted')}><Loader2 size={16} className="animate-spin" /></div>
  if (item.photo_url) {
    const extra = (item.photos?.length || 0) - 1
    return (
      <button type="button" onClick={onView} aria-label={`view photos of ${item.name}`} className={cn(base, 'relative overflow-hidden border border-brand-border')}>
        <img src={item.photo_url} alt={item.name} loading="lazy" className="h-full w-full object-cover" />
        {extra > 0 && <span className="absolute -right-1 -top-1 rounded-full bg-brand-primary px-1 text-[9px] font-bold text-white shadow">+{extra}</span>}
      </button>
    )
  }
  return (
    <button type="button" onClick={onShoot} title="Add a photo" aria-label={`add a photo of ${item.name}`}
      className={cn(base, 'border-2 border-dashed border-amber-300 bg-amber-50 text-amber-600 hover:bg-amber-100')}>
      <Camera size={16} />
    </button>
  )
}

// Photo GALLERY for the catalogue — the current photo big, a filmstrip of ALL photos (newest first)
// each labelled with its date, "Add another photo" (appends), and a per-photo delete for the owner.
function PhotoViewer({ item, isOwner, onClose, onAddPhoto, onDeletePhoto, onSavePhotoNote }) {
  const photos = (item.photos && item.photos.length)
    ? item.photos
    : (item.photo_url ? [{ id: 0, url: item.photo_url, taken_at: item.photo_taken_at }] : [])
  const [selId, setSelId] = useState(photos[0]?.id)
  useEffect(() => {
    if (!photos.some((p) => p.id === selId)) setSelId(photos[0]?.id)
  }, [item.photos]) // eslint-disable-line react-hooks/exhaustive-deps
  const sel = photos.find((p) => p.id === selId) || photos[0]
  if (!sel) return null
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/90 p-4" onClick={onClose}>
      <div className="flex items-start justify-between gap-3 text-white">
        <div className="min-w-0">
          <p className="truncate font-medium">{item.name}</p>
          <p className="text-xs text-white/60">
            {item.ref} · {photos.length} photo{photos.length === 1 ? '' : 's'}
            {sel.taken_at && ` · shown ${sel.taken_at.slice(0, 10)}`}
            {item.photo_taken_by && ` · latest by ${item.photo_taken_by}`}
          </p>
        </div>
        <button onClick={onClose} aria-label="close" className="rounded-full p-2 text-white/80 hover:bg-white/10"><X size={22} /></button>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center py-3">
        <img src={sel.url} alt={item.name} className="max-h-full max-w-full rounded-xl object-contain" onClick={(e) => e.stopPropagation()} />
      </div>
      {onSavePhotoNote && sel.id !== 0 && (
        <div className="mb-3" onClick={(e) => e.stopPropagation()}>
          <PhotoNoteEditor key={sel.id} note={sel.note} onSave={(v) => onSavePhotoNote(sel.id, v)} />
        </div>
      )}
      <div className="mb-3 flex gap-3 overflow-x-auto pb-1" onClick={(e) => e.stopPropagation()}>
        {photos.map((p) => (
          <div key={p.id} className="shrink-0 text-center">
            <div className="relative">
              <button type="button" onClick={() => setSelId(p.id)}
                className={cn('block h-16 w-16 overflow-hidden rounded-lg border-2', p.id === sel.id ? 'border-white' : 'border-white/25')}>
                <img src={p.url} alt="" className="h-full w-full object-cover" />
              </button>
              {isOwner && p.id !== 0 && (
                <button type="button" onClick={() => onDeletePhoto(p.id)} aria-label="delete this photo"
                  className="absolute -right-1.5 -top-1.5 rounded-full bg-red-600 p-1 text-white shadow active:scale-90">
                  <Trash2 size={12} />
                </button>
              )}
            </div>
            {p.taken_at && <span className="mt-1 block text-[9px] text-white/50">{p.taken_at.slice(0, 10)}</span>}
          </div>
        ))}
      </div>
      <button onClick={(e) => { e.stopPropagation(); onAddPhoto() }} className="mx-auto flex items-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-semibold text-brand-ink"><Camera size={18} /> Add another photo</button>
    </div>
  )
}

// Persistent item NOTES — owner edits here; counters write the same note on the count screen. Autosaves
// on a ~800ms debounce and on blur; shows who last saved it and when.
function NotesEditor({ item, canEdit, onSave, onClose }) {
  const [text, setText] = useState(item.notes || '')
  const [state, setState] = useState('idle') // idle | saving | saved
  const timer = useRef(null)
  const lastSaved = useRef(item.notes || '')
  const doSave = useCallback(async (val) => {
    if (val === lastSaved.current) return
    setState('saving')
    const ok = await onSave(val)
    if (ok) { lastSaved.current = val; setState('saved') } else { setState('idle') }
  }, [onSave])
  function onChange(v) {
    setText(v)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => doSave(v), 800)
  }
  function flushSave() { if (timer.current) clearTimeout(timer.current); doSave(text) }
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" onClick={() => { flushSave(); onClose() }}>
      <div className="w-full max-w-md rounded-t-2xl bg-white p-4 shadow-xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="truncate font-semibold text-brand-ink">Note · {item.name}</h3>
          <button onClick={() => { flushSave(); onClose() }} aria-label="Close" className="rounded-lg p-1 text-brand-muted hover:bg-brand-surface"><X size={18} /></button>
        </div>
        <textarea autoFocus disabled={!canEdit} value={text} onChange={(e) => onChange(e.target.value)} onBlur={flushSave} rows={5}
          placeholder="e.g. the 100 ml one · kept in the back cupboard · supplier discontinued the blue box"
          className={INPUT} />
        <div className="mt-1 flex items-center justify-between text-[11px] text-brand-muted">
          <span>{state === 'saving' ? 'saving…' : state === 'saved' ? 'saved' : (item.notes_updated_by ? `${item.notes_updated_by} · ${(item.notes_updated_at || '').slice(0, 10)}` : 'no note yet')}</span>
          <span>{text.length}/4000</span>
        </div>
      </div>
    </div>
  )
}

// Same reasoning as the count screen: no vips/imagemagick in the container, and a raw camera file is
// 3-5 MB. Resize in the browser to ~1000 px / JPEG 0.75 ≈ 150 KB before it ever leaves the device.
async function downscale(file, maxPx = 1000, quality = 0.75) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      const scale = Math.min(1, maxPx / Math.max(img.width, img.height))
      const w = Math.max(1, Math.round(img.width * scale))
      const h = Math.max(1, Math.round(img.height * scale))
      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      canvas.getContext('2d').drawImage(img, 0, 0, w, h)
      URL.revokeObjectURL(url)
      canvas.toBlob((b) => resolve(b && b.size < file.size ? b : file), 'image/jpeg', quality)
    }
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file) }
    img.src = url
  })
}

function toggleActive(i) {
  if (i.active) {
    if (!confirm(`Deactivate ${i.name}? It leaves new counts + the export but history is kept.`)) return
    router.delete(`/stock_items/${i.id}`, { onSuccess: () => toast.success('Deactivated') })
  } else {
    router.patch(`/stock_items/${i.id}`, { stock_item: { active: true } }, { onSuccess: () => toast.success('Reactivated') })
  }
}

function EditRow({ initial, categories = [], onDone, isNew }) {
  const [f, setF] = useState({ ...BLANK, ...initial, category: initial.category ?? '', par_level: initial.par_level ?? '' })
  const [busy, setBusy] = useState(false)
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }))
  function save() {
    if (!f.ref.trim() || !f.name.trim()) { toast.error('Ref and name are required'); return }
    setBusy(true)
    const payload = { stock_item: f }
    const opts = { onSuccess: () => { toast.success(isNew ? 'Added' : 'Saved'); onDone() }, onError: (e) => { toast.error(Object.values(e || {})[0] || 'Failed'); setBusy(false) } }
    isNew ? router.post('/stock_items', payload, opts) : router.patch(`/stock_items/${initial.id}`, payload, opts)
  }
  return (
    <div className="border-y border-brand-primary/30 bg-brand-surface/60 p-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Lbl t="Ref *"><input value={f.ref} onChange={set('ref')} className={INPUT} /></Lbl>
        <Lbl t="Name *"><input value={f.name} onChange={set('name')} className={INPUT} /></Lbl>
        <Lbl t="Category">
          <input list="cat-edit-list" value={f.category || ''} onChange={set('category')} placeholder="e.g. Endodontics" className={INPUT} />
          <datalist id="cat-edit-list">{(categories || []).map((c) => <option key={c} value={c} />)}</datalist>
        </Lbl>
        <Lbl t="Storage area"><input value={f.storage_area} onChange={set('storage_area')} className={INPUT} /></Lbl>
        <Lbl t="Unit"><input value={f.unit} onChange={set('unit')} className={INPUT} /></Lbl>
        <Lbl t="Supplier"><input value={f.supplier} onChange={set('supplier')} className={INPUT} /></Lbl>
        <Lbl t="Supplier code"><input value={f.supplier_code} onChange={set('supplier_code')} className={INPUT} /></Lbl>
        <Lbl t="Barcode"><input value={f.barcode || ''} onChange={set('barcode')} className={INPUT} /></Lbl>
        <Lbl t="Par level"><input inputMode="decimal" value={f.par_level} onChange={set('par_level')} className={INPUT} /></Lbl>
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <button onClick={onDone} className="rounded-xl border border-brand-border px-4 py-2 text-sm">Cancel</button>
        <button onClick={save} disabled={busy} className="rounded-xl bg-brand-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{busy ? 'Saving…' : 'Save'}</button>
      </div>
    </div>
  )
}

function Lbl({ t, children }) {
  return <label className="block"><span className="mb-0.5 block text-[11px] font-medium text-brand-muted">{t}</span>{children}</label>
}

// Per-photo note inside the viewer — opening a photo shows its note; edit + autosave on ~800ms debounce
// and on blur. Everyone can write it; the returned photos[] fans out to the row + the open viewer.
function PhotoNoteEditor({ note, onSave }) {
  const [text, setText] = useState(note || '')
  const [state, setState] = useState('idle') // idle | saving | saved
  const timer = useRef(null)
  const lastSaved = useRef(note || '')
  const doSave = useCallback(async (val) => {
    if (val === lastSaved.current) return
    setState('saving')
    const ok = await onSave(val)
    if (ok) { lastSaved.current = val; setState('saved') } else { setState('idle') }
  }, [onSave])
  function onChange(v) {
    setText(v)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => doSave(v), 800)
  }
  function flushSave() { if (timer.current) clearTimeout(timer.current); doSave(text) }
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])
  return (
    <div className="rounded-xl bg-white/10 p-2">
      <textarea value={text} onChange={(e) => onChange(e.target.value)} onBlur={flushSave} rows={2}
        placeholder="Note for this photo — e.g. front label · batch / lot no. · which variant"
        className="w-full resize-none rounded-lg border border-white/20 bg-black/30 px-3 py-2 text-sm text-white placeholder-white/40 focus:border-white/50 focus:outline-none" />
      <div className="mt-1 text-right text-[11px] text-white/50">{state === 'saving' ? 'saving…' : state === 'saved' ? 'saved' : 'photo note'}</div>
    </div>
  )
}

// ── per-item history timeline (read-only, append-only log) ─────────────────
const LOG_META = {
  note:             { icon: StickyNote,    label: 'Note' },
  supplier_changed: { icon: Truck,         label: 'Supplier changed' },
  photo_added:      { icon: Camera,        label: 'Photo added' },
  photo_removed:    { icon: Trash2,        label: 'Photo removed' },
  created:          { icon: PlusCircle,    label: 'Created' },
  cancelled:        { icon: Ban,           label: 'Cancelled' },
  restored:         { icon: RotateCcw,     label: 'Restored' },
  photo_noted:      { icon: MessageSquare, label: 'Photo note' },
}

function friendlyAt(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return String(iso).slice(0, 16).replace('T', ' ')
  return d.toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

// Read-only timeline for one item: newest-first, kind-appropriate icon + body + who + friendly time.
function HistorySheet({ item, onClose }) {
  const list = item.logs || []
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-t-2xl bg-white p-4 shadow-xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="truncate font-semibold text-brand-ink">History · {item.name}</h3>
          <button onClick={onClose} aria-label="Close" className="rounded-lg p-1 text-brand-muted hover:bg-brand-surface"><X size={18} /></button>
        </div>
        {list.length === 0 ? (
          <p className="py-6 text-center text-sm text-brand-muted">No history yet.</p>
        ) : (
          <ul className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
            {list.map((l, idx) => {
              const meta = LOG_META[l.kind] || { icon: History, label: l.kind }
              const Icon = meta.icon
              return (
                <li key={idx} className="flex gap-3">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-surface text-brand-muted"><Icon size={14} /></span>
                  <div className="min-w-0">
                    <p className="text-sm text-brand-ink">{l.body || meta.label}</p>
                    <p className="text-[11px] text-brand-muted">{[l.actor, friendlyAt(l.at)].filter(Boolean).join(' · ')}</p>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
